import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {FmlxIcon} from 'fmlx-common-ui';
import {getUsersWithLicenseCheck} from 'api/protocolManagementApi';
import _ from 'lodash';

import {
  BaseProtocolMenu,
  ProtocolExplorerAccordionGroup,
  ProtocolExplorerFilterType,
  ProtocolExplorerSortingAccessor,
} from 'constants/protocolExplorerEnum';
import protocolExplorerBusiness from 'stores/business/protocolExplorerBusiness';
import {selectIsInstrumentOperational} from 'stores/selectors/instrumentSelectors';
import {showModalType} from 'stores/actions/typedModalAction';
import BreadCrumbs from './BreadCrumbs';
import ExplorerHeader from './ProtocolExplorerHeader';
import RenameProtocolModal from './RenameProtocolModal';
import CreateFolderModal from './CreateFolderModal';
import {ThumbnailView, ListView, DetailView} from './View';
import SendCopyModal from './SendCopyModal';
import {PageLayout} from '../../components';
import withIntl from '../../lib/withIntl';
import {checkContainIllegalChars} from '../../lib/stringHelper';

import {hideLoading, showLoading, showSnackbar} from '../../stores/actions/appStateAction';
import {showModal, hideModal} from '../../stores/actions/modalStateAction';
import {setLastOpenedProtocol} from '../../stores/actions/protocolAction';
import {
  showImportUnknownPlateId,
  showSelectedProtocolHasTessaraProtocolOnDropOnSharedFolder,
} from '../../stores/business/modalStateBusinessAction';
import {
  downloadProtocol,
  newProtocol,
  openProtocol,
  renameProtocol,
  uploadProtocolZip,
  updateProtocolState,
  createProtocolFolder,
  renameProtocolFolder,
  deleteProtocolsAndFolders,
  cutProtocolsAndFoldersToClipboard,
  copyProtocolsAndFoldersToClipboard,
  pasteProtocolsAndFoldersFromClipboard,
  sendProtocolsAndFoldersToUsers,
  moveProtocolsAndFolders,
  confirmUploadProtocolZip,
  shareUnshareProtocolsAndFolders,
  NEW_PROTOCOL_ID,
  isFolderContainsTessaraProtocol,
  cancelUploadProtocol,
  checkTessaraProtocolDroppedInSharedFolder,
} from '../../stores/business/protocolManagementBusiness';
import {recoverRunningProtocol} from '../../stores/business/runtimeBusiness';
import {isRoleLevel, setExplorerViewSettings} from '../../stores/business/userManagementBusiness';

import {
  ExplorerViewMode,
  UserRoleLevel,
  Feedback,
  ServiceResponseCode,
  FastRobotErrorcode,
  UserRole,
  ExplorerContentTypes,
  ClipboardEventType,
  MappedLabwareAction,
  ServiceFastErrorcode,
  CommonButtonLabel,
  SortDirectionType,
  ModalType,
} from '../../constants/dataEnum';
import './ProtocolExplorer.scss';
import {refreshLabwareDefinitions} from '../../stores/business/labwareDatabaseBusiness';
import {globalUndoRedoClearItem} from '../../stores/business/undoRedoBusinessAction';
import {setErrorCodeNumber} from '../../stores/business/commonBusiness';
import {
  showComingSoonDialog,
  showModalConvertToTessaraProtocol,
  showUpdatePlateDefinitionDialog,
} from '../../stores/business/commonModalBusiness';
import {useKeyPress} from '../../lib/customHooks';
import RemoveProtocolModal from './RemoveProtocolModal';
import PlateSelectionModal from './PlateSelectionModal';
import {getDataProtocolsAndFolders, getProtocolsAndFolders} from '../../lib/protocolHelper';
import {
  createDownloadFile,
  generateExportedItemsFileName,
  getExplorerSelectionData,
  getProtocolIndexFromList,
  protocolExplorerHelper,
} from '../../lib/explorerHelper';
import SelectableArea from '../../components/SelectableArea/SelectableArea';
import DragPreviewLayer from '../../components/DragPreviewLayer/DragPreviewLayer';
import {
  resetSelectedProtocolOrFolder,
  setCurrentProtocolFolder,
  setProtocolExplorerExpandedGroups,
  setProtocolExplorerFilter,
  setProtocolExplorerSort,
  setSelectedProtocolOrFolder,
} from '../../stores/actions/protocolExplorerAction';
import {resetProtocolPendingSaveStates} from '../../stores/business/protocolEditorBusiness';
import {isLabwareAlreadyExist} from '../../stores/business/labwareBusinessAction';
import {formatLabware} from '../../stores/actions/availableLabwareStateAction';
import {proceedReteachImportedProtocol} from '../../stores/business/reteachPlateBusiness';
import FormattedHTMLMessage from '../../components/FormattedHTMLMessage';
import {toBase64Promise} from '../../lib/fileHelper';
import {getAppLoading} from '../../stores/selectors/appStateSelectors';
import {getIsOperator, getSessionUser} from '../../stores/selectors/sessionSelectors';
import {getUserManagementAvailableRoles} from '../../stores/selectors/userManagementSelectors';
import {
  getAvailableProtocols,
  getLastOpenedProtocol,
  getLastProtocolCheckpoint,
} from '../../stores/selectors/protocolSelectors';
import {
  getExpandedGroups,
  getProtocolExplorerState,
  getSelectedProtocolAndFolderIds,
  selectProtocolExplorerActiveFilter,
  selectProtocolExplorerActiveSort,
  selectProtocolExplorerItems,
  selectTrayBoundProtocolsIDs,
} from '../../stores/selectors/protocolExplorerSelectors';
import {getProtocolConfig} from '../../stores/selectors/configSelectors';
import {getProtocolEditorAutosave} from '../../stores/selectors/protocolEditorSelectors';
import {getTrayCalibrationTrayID} from '../../stores/selectors/trayCalibrationSelectors';
import {getLicenseIsTessaraInstrument} from '../../stores/selectors/licenseSelectors';
import {getExportProtocolErrorMessage} from '../../stores/business/commonProtocolManagementBusiness';
import ProtocolContextMenu from './ProtocolContextMenu';

const DefaultBreadcrumbs = Object.freeze([
  {
    id: 0,
    protocolFolderId: null,
    label: 'My Explorer',
    icon: <FmlxIcon name="Folder" />,
  },
]);

function ProtocolExplorer(props) {
  const dispatch = useDispatch();

  const appLoading = useSelector(getAppLoading);
  const sessionUser = useSelector(getSessionUser);
  const userManagementAvailableRoles = useSelector(getUserManagementAvailableRoles);
  const availableProtocols = useSelector(getAvailableProtocols);
  const lastOpenedProtocol = useSelector(getLastOpenedProtocol);
  const lastProtocolCheckpoint = useSelector(getLastProtocolCheckpoint);
  const {
    runningProtocol,
    protocolClipboardEventType,
    protocolClipboardItems,
    currentProtocolFolder,
    protocolFolderList,
    selectedProtocolOrFolderIDs,
    defaultViewSettings,
  } = useSelector(getProtocolExplorerState);
  const expandedGroups = useSelector(getExpandedGroups);
  const selectedProtocolAndFolderIds = useSelector(getSelectedProtocolAndFolderIds);
  const protocolConfig = useSelector(getProtocolConfig);
  const protocolEditorStateAutosave = useSelector(getProtocolEditorAutosave);
  const trayID = useSelector(getTrayCalibrationTrayID);
  const licenseStateIsTessaraInstrument = useSelector(getLicenseIsTessaraInstrument);
  const isOperatorRoleLoggedIn = useSelector(getIsOperator);
  const isInstrumentOperational = useSelector(selectIsInstrumentOperational);
  const trayProtocolIds = useSelector(selectTrayBoundProtocolsIDs);
  const explorerItems = useSelector(selectProtocolExplorerItems);
  const activeFilter = useSelector(selectProtocolExplorerActiveFilter);
  const activeSort = useSelector(selectProtocolExplorerActiveSort);

  const canUserPasteProtocolsFolders = useMemo(() => {
    const isSelectedFoldersSharedByOtherUser = availableProtocols.filter(
      (ele) =>
        selectedProtocolOrFolderIDs.includes(ele.ID) &&
        ele.type === ExplorerContentTypes.FOLDER &&
        ele[ele.type].owner !== sessionUser.userId,
    );

    const isOnRoot = !currentProtocolFolder;
    const isFolderOwner = protocolFolderList[protocolFolderList.length - 1]?.owner === sessionUser.userId;

    return (isOnRoot || isFolderOwner) && isSelectedFoldersSharedByOtherUser.length === 0;
  }, [currentProtocolFolder, protocolFolderList, sessionUser.userId, selectedProtocolOrFolderIDs, availableProtocols]);

  const {intl} = props;

  const {explorerViewMode} = defaultViewSettings;

  const [showProtocolInfo] = useState(false);
  const [renameProtocolModalVisible, setRenameProtocolModalVisible] = useState(false);
  const [sendProtocolModal, setSendProtocolModal] = useState({visible: false, isRunOnly: false});
  const [availableUsers, setAvailableUsers] = useState([]);
  const [createFolderModalVisible, setCreateFolderModalVisible] = useState(false);
  const [toSelectNewProtocolIds, setToSelectNewProtocolIds] = useState([]);
  const [removeProtocolModalVisible, setRemoveProtocolModalVisible] = useState(false);
  const [plateSelectionModalVisisble, setPlateSelectionModalVisible] = useState(false);
  const [plateSelectionModalData, setPlateSelectionModalData] = useState([]);
  const [uploadKey, setUploadKey] = useState('');

  const runningProtocolId = runningProtocol?.id || null;
  const runningFolderIds = runningProtocol?.protocolFolderLineages?.map((e) => e.id) || Array(0);

  const clipboardProtocolIds = protocolClipboardEventType ? protocolClipboardItems.map((e) => e.ID) : [];
  const greyedOutProtocolIds =
    protocolClipboardEventType === ClipboardEventType.CUT && clipboardProtocolIds?.length
      ? [...clipboardProtocolIds]
      : [];

  const breadcrumbs = useMemo(() => {
    if (!currentProtocolFolder) {
      return DefaultBreadcrumbs;
    }

    const newBreadcrumbs = protocolFolderList.map((item, idx) => ({
      id: Math.max(DefaultBreadcrumbs.map((e) => e.id)) + idx + 1,
      protocolFolderId: item.id,
      label: item.name,
      icon: <FmlxIcon name="Folder" />,
    }));
    return [...DefaultBreadcrumbs, ...newBreadcrumbs];
  }, [currentProtocolFolder, protocolFolderList]);

  const showBreadCrumbs = useMemo(() => breadcrumbs.length > 1, [breadcrumbs]);

  const ctrlKeyPressed = useKeyPress('Control');
  const shiftKeyPressed = useKeyPress('Shift');

  const lastSelectedProtocolIdx = useRef(-1);

  const protocolContentRef = useRef(null);
  const sendCopyModalRef = useRef(null);
  const protocolItemsRef = useRef([]);

  const onSortProtocol = () => {
    const {direction} = activeSort;
    const newSortDirection = direction === SortDirectionType.ASC ? SortDirectionType.DESC : SortDirectionType.ASC;

    dispatch(setProtocolExplorerSort({direction: newSortDirection, accessor: ProtocolExplorerSortingAccessor.NAME}));
  };

  const refreshExplorer = useCallback(async () => {
    const loadingMessage = intl.formatMessage({id: 'explorer.loading.loadingProtocol'});
    dispatch(showLoading(loadingMessage));

    if (isInstrumentOperational) await dispatch(protocolExplorerBusiness.reloadInstrumentCondition());

    await doFetchUsers();
    const fetchResults = await dispatch(protocolExplorerBusiness.fetchAndSetExplorerItems(currentProtocolFolder?.id));

    dispatch(hideLoading(loadingMessage));
    dispatch(protocolExplorerBusiness.validateRefetechedExplorerItems(fetchResults));
  }, [currentProtocolFolder?.id, dispatch, intl, isInstrumentOperational]);

  const processedExplorerItems = useMemo(() => {
    const {direction: sortDirection, accessor: sortAccessor} = activeSort;
    const isReverseSorting = sortDirection === SortDirectionType.DESC;

    const filteredItems = protocolExplorerHelper.filterItems(explorerItems, activeFilter, trayProtocolIds);
    const filteredAndSortedItems = protocolExplorerHelper.sortItems(filteredItems, isReverseSorting, sortAccessor);

    const selectedItemsList = [];

    const explorerItemsWithIsSelectedFlag = filteredAndSortedItems.map((e) => {
      const isSelected = selectedProtocolAndFolderIds.indexOf(e.ID) !== -1;

      if (isSelected) selectedItemsList.push(e);
      return {...e, isSelected};
    });

    return {protocolList: explorerItemsWithIsSelectedFlag, selectedItems: selectedItemsList};
  }, [activeFilter, activeSort, explorerItems, selectedProtocolAndFolderIds, trayProtocolIds]);

  const {protocolList, selectedItems} = processedExplorerItems;

  const setFilter = (type, value, resetSelectedItems = true) => {
    if (resetSelectedItems) {
      dispatch(resetSelectedProtocolOrFolder());
    }
    dispatch(setProtocolExplorerFilter({type, value}));
  };

  const onFilterProtocolByName = (value) => {
    setFilter(ProtocolExplorerFilterType.NAME, value, false);
  };

  const onFilterByShareProtocolAndFolder = () => {
    dispatch(setProtocolExplorerExpandedGroups({[ProtocolExplorerAccordionGroup.FOLDERS]: true}));
    setFilter(ProtocolExplorerFilterType.SHARED_ITEMS);
  };

  const onShowAllProtocol = () => {
    setFilter(ProtocolExplorerFilterType.ALL_ITEMS);
  };

  const onFilterProtocolByTrayID = async () => {
    setFilter(ProtocolExplorerFilterType.SAVED_IN_TRAY);
  };

  const onFilterProtocolByTrayType = async (trayType) => {
    dispatch(setProtocolExplorerExpandedGroups({[ProtocolExplorerAccordionGroup.FOLDERS]: false}));
    setFilter(ProtocolExplorerFilterType.TRAY_TYPE, trayType);
  };

  const onFilterTessaraProtocol = async () => {
    setFilter(ProtocolExplorerFilterType.TESSARA_PROTOCOLS);
  };

  const selectMultiProtocols = useCallback(
    (IDs, isResetOther = true) => {
      const newSelectedIds = isResetOther ? IDs : [...IDs, ...selectedProtocolAndFolderIds];

      if (!IDs.length) {
        lastSelectedProtocolIdx.current = -1;
      }

      dispatch(setSelectedProtocolOrFolder(newSelectedIds));
    },
    [dispatch, selectedProtocolAndFolderIds],
  );

  // WORKAROUND: select new protocol based on toSelectNewProtocolIds,
  // to make sure selectMultiProtocols already have the updated protocolList
  useEffect(() => {
    if (toSelectNewProtocolIds?.length) {
      const index = getProtocolIndexFromList(protocolList, toSelectNewProtocolIds[0]);
      lastSelectedProtocolIdx.current = index;

      selectMultiProtocols(toSelectNewProtocolIds);
      setToSelectNewProtocolIds([]);
    }
  }, [toSelectNewProtocolIds, protocolList, selectMultiProtocols]);

  const removeProtocolClick = () => {
    setRemoveProtocolModalVisible(true);
  };

  const renameProtocolHeaderClick = () => {
    setRenameProtocolModalVisible(true);
  };

  const doRenameProtocol = async (protocol, newName) => {
    const lastOpenedProtocolId = lastOpenedProtocol?.id;

    const loadingMessage = intl.formatMessage({
      id: 'explorer.explorerMenuBarContainer.renameProtocol.loadingMessage',
    });

    dispatch(showLoading(loadingMessage));

    const response = await dispatch(renameProtocol(protocol, newName));

    if (!response) {
      dispatch(hideLoading());
      return;
    }

    // update last opened protocol
    if (protocol.id === lastOpenedProtocolId) {
      dispatch(setLastOpenedProtocol(response));
    }

    setRenameProtocolModalVisible(false);
    refreshExplorer();
    dispatch(hideLoading());
  };

  const doRenameProtocolFolder = async (folder, newName) => {
    const loadingMessage = intl.formatMessage({
      id: 'explorer.explorerMenuBarContainer.renameFolder.loadingMessage',
    });

    dispatch(showLoading(loadingMessage));

    const response = await dispatch(renameProtocolFolder(folder, newName));

    if (!response) {
      dispatch(hideLoading());
      return;
    }

    setRenameProtocolModalVisible(false);
    refreshExplorer();
    dispatch(hideLoading());
  };

  const renameProtocolModalRenameClick = (newName) => {
    const selectedItem = selectedItems[0];
    const isFolder = selectedItem.type === ExplorerContentTypes.FOLDER;

    if (isFolder) {
      const {folder} = selectedItem;
      doRenameProtocolFolder(folder, newName);
    } else {
      const {protocol} = selectedItem;
      doRenameProtocol(protocol, newName);
    }
  };

  const renameProtocolModalCloseClick = () => {
    setRenameProtocolModalVisible(false);
  };

  const downloadProtocolClick = useCallback(async () => {
    // protocol name contains illegal characters
    if (selectedItems.map((e) => e.name).find((e) => checkContainIllegalChars(e))) {
      dispatch(showSnackbar(Feedback.ERROR, intl.formatMessage({id: 'explorer.download.illegalCharacters'})));

      return;
    }

    let type = 'item';
    if (selectedItems.every((p) => p.type === ExplorerContentTypes.FOLDER)) {
      type = 'folder';
    } else if (selectedItems.every((p) => p.type === ExplorerContentTypes.PROTOCOL)) {
      type = 'protocol';
    }
    const loadingMessage = intl.formatMessage(
      {
        id: 'explorer.explorerMenuBarContainer.downloadProtocol.loadingMessage',
      },
      {type},
    );

    dispatch(showLoading(loadingMessage));

    const {protocols, folders} = getDataProtocolsAndFolders(selectedItems);
    const protocolIds = protocols.map((p) => p.id);
    const folderIds = folders.map((p) => p.id);

    const response = await dispatch(downloadProtocol(protocolIds, folderIds));

    if (!response?.size) {
      dispatch(showSnackbar(Feedback.ERROR, intl.formatMessage({id: 'explorer.download.error'})));
      dispatch(hideLoading());

      return;
    }

    if (response.type === 'text/plain') {
      const reader = new FileReader();
      reader.onload = () => {
        const decoder = new TextDecoder('utf-8');
        const convertedResponse = new Uint8Array(reader.result);

        const responseCode = convertedResponse[0];
        const payload = decoder.decode(convertedResponse.slice(1));

        if (responseCode !== ServiceResponseCode.OK) {
          dispatch(showSnackbar(Feedback.ERROR, getExportProtocolErrorMessage(responseCode, payload)));
        }

        dispatch(hideLoading());
      };
      reader.readAsArrayBuffer(response);
      return;
    }

    if (response && response instanceof Blob) {
      const {downloadFileNameMaxLength} = protocolConfig;
      const fileName = generateExportedItemsFileName(selectedItems, downloadFileNameMaxLength);

      createDownloadFile(response, fileName, '.zip');
    }

    dispatch(hideLoading());
  }, [dispatch, intl, protocolConfig, selectedItems]);

  const showFailedUploadModal = async (payload) => {
    const {protocols} = payload;

    await dispatch(setErrorCodeNumber(payload.fastErrorMessage));

    if (payload.fastErrorMessage.toLowerCase() === FastRobotErrorcode.UNKNOWN_PLATE_ID.toLowerCase()) {
      dispatch(showImportUnknownPlateId());

      return;
    }

    dispatch(
      showModal(
        intl.formatMessage({
          id: 'explorer.explorerMenuBarContainer.uploadProtocol.fail.title',
        }),
        intl.formatMessage(
          {id: 'explorer.explorerMenuBarContainer.uploadProtocol.fail.message'},
          {protocolName: protocols[0].name},
        ),
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              dispatch(hideModal());
            },
            text: intl.formatMessage({
              id: 'explorer.explorerMenuBarContainer.uploadProtocol.fail.okButton',
            }),
          },
        ],
        () => dispatch(hideModal()),
        'upload-fail-modal',
        true,
      ),
    );
  };

  const confirmUploadProtocol = async (mappedLabwareIDs, key = null, callback = () => {}) => {
    const response = await dispatch(confirmUploadProtocolZip(key ?? uploadKey, mappedLabwareIDs));

    // Show Modal Reteach If there's new Microplate Definitions
    if (response?.payload?.newMicroplateDefinitions?.length > 0 && isInstrumentOperational) {
      dispatch(
        showUpdatePlateDefinitionDialog(
          intl.formatMessage({
            id: 'modal.showUpdatePlateDefinitionDialog.title',
          }),
          () => {
            dispatch(proceedReteachImportedProtocol(response?.payload?.newMicroplateDefinitions));
          },
        ),
      );
    }

    if (
      response?.payload?.fastErrorCode === ServiceFastErrorcode.UNKNOWN_PLATE_ID ||
      response?.payload?.fastErrorCode === ServiceFastErrorcode.MISMATCH_PLATE_DEFINITION_AND_WELL_TARGET
    ) {
      // get updated labware list
      await dispatch(refreshLabwareDefinitions());
      dispatch(hideLoading());

      dispatch(
        showSnackbar(
          Feedback.ERROR,
          <FormattedHTMLMessage id="explorer.explorerMenuBarContainer.uploadProtocol.fail.invalidValue" />,
          0,
          false,
          true,
          () => {},
          <FmlxIcon name="CloseOutline" size="xs" />,
        ),
      );

      return;
    }

    if (response.code !== ServiceResponseCode.OK) {
      callback();
      dispatch(
        showSnackbar(
          Feedback.ERROR,
          intl.formatMessage({
            id: 'explorer.explorerMenuBarContainer.uploadProtocol.fail.invalidFile',
          }),
        ),
      );
      dispatch(hideLoading());

      return;
    }

    callback();
    await refreshExplorer();
    dispatch(refreshLabwareDefinitions());

    const newProtocols = response.payload.protocols || [];
    const newProtocolFolders = response.payload.protocolFolders || [];
    setToSelectNewProtocolIds([...newProtocols, ...newProtocolFolders].map((item) => item.id));

    dispatch(hideLoading());

    const snackbarMessage = intl.formatMessage(
      {id: 'explorer.explorerMenuBarContainer.uploadProtocol.successMessage'},
      {protocolCount: newProtocols.length},
    );

    dispatch(showSnackbar(Feedback.SUCCESS, snackbarMessage));
  };

  const uploadProtocolZipClick = async (e) => {
    if (!e.target.files.length) {
      return;
    }

    const loadingMessage = intl.formatMessage({
      id: 'explorer.explorerMenuBarContainer.uploadProtocol.loadingMessage',
    });

    dispatch(showLoading(loadingMessage, cancelUploadProtocol));

    const files = [];
    const base64Files = [];

    for (let idx = 0; idx < e.target.files.length; idx += 1) {
      const file = e.target.files[idx];
      files.push(file);
    }

    const promises = files.map(async (file) => base64Files.push(await toBase64Promise(file)));
    await Promise.all(promises);

    const response = await dispatch(uploadProtocolZip(base64Files));

    if (!response) return;

    if (response.code === ServiceResponseCode.OK) {
      // get updated labware list
      await dispatch(refreshLabwareDefinitions());

      const {microplateDefinitions, uploadKey: uploadKeyRes} = response.payload;

      const formatedLabwareDefinition = microplateDefinitions.map((lab) => {
        const formattedLabware = formatLabware(lab);
        const labwareAlreadyExist = dispatch(isLabwareAlreadyExist(lab));

        if (labwareAlreadyExist) {
          formattedLabware.LabwareID = labwareAlreadyExist.LabwareID;
          formattedLabware.isExist = true;
        }
        return formattedLabware;
      });

      const allLabwaresAlreadyExist =
        formatedLabwareDefinition.filter((el) => el.isExist).length === formatedLabwareDefinition.length;

      if (allLabwaresAlreadyExist) {
        const mappedLabwareIDs = formatedLabwareDefinition.map((lab) => ({
          Action: MappedLabwareAction.USE_EXISTING,
          ID: lab.LabwareID,
          NewName: lab.name,
          Hash: lab.hash,
        }));

        confirmUploadProtocol(mappedLabwareIDs, uploadKeyRes);
      } else {
        dispatch(hideLoading());
        setUploadKey(uploadKeyRes);
        setPlateSelectionModalData(formatedLabwareDefinition);
        setPlateSelectionModalVisible(true);
      }
      return;
    }

    if (response.code === ServiceResponseCode.NOT_VALID_CSV) {
      dispatch(hideLoading());
      showFailedUploadModal(response.payload);
      return;
    }

    if (response.code === ServiceResponseCode.NOT_VALID_FILE) {
      dispatch(
        showSnackbar(
          Feedback.ERROR,
          intl.formatMessage({
            id: 'explorer.explorerMenuBarContainer.uploadProtocol.fail.invalidFile',
          }),
        ),
      );
      dispatch(hideLoading());

      return;
    }

    if (response.code === ServiceResponseCode.UNABLE_TO_SYNC_LABWARE) {
      dispatch(
        showSnackbar(
          Feedback.ERROR,
          intl.formatMessage({
            id: 'explorer.explorerMenuBarContainer.uploadProtocol.fail.unableToSyncLabware',
          }),
        ),
      );
      dispatch(hideLoading());

      return;
    }

    if (response.code === ServiceResponseCode.ZIP_FILE_IMPORT_NOT_ENOUGH_PRIVILEGE) {
      dispatch(
        showSnackbar(
          Feedback.ERROR,
          intl.formatMessage({
            id: 'explorer.explorerMenuBarContainer.uploadProtocol.fail.doesNotHavePrivilege',
          }),
        ),
      );
      dispatch(hideLoading());

      return;
    }

    if (response.code === ServiceResponseCode.REACH_FOLDER_LEVEL_LIMIT) {
      dispatch(
        showSnackbar(
          Feedback.ERROR,
          intl.formatMessage({
            id: 'explorer.explorerMenuBarContainer.uploadProtocol.fail.reachFolderLevelLimit',
          }),
        ),
      );
      dispatch(hideLoading());

      return;
    }

    if (response.code === ServiceResponseCode.MAX_UPLOAD_FILE_SIZE_REACHED) {
      dispatch(
        showModal(
          intl.formatMessage({
            id: 'explorer.explorerMenuBarContainer.uploadProtocol.fail.reachedMaxisize.title',
          }),
          intl.formatMessage({
            id: 'explorer.explorerMenuBarContainer.uploadProtocol.fail.reachedMaxisize.text',
          }),
          [
            {
              className: 'btn-lg',
              style: 'primary',
              handlerClick: () => {
                dispatch(hideModal());
              },
              text: intl.formatMessage({id: CommonButtonLabel.GOT_IT}),
            },
          ],
        ),
      );
      dispatch(hideLoading());

      return;
    }

    if (response.code !== ServiceResponseCode.OK) {
      dispatch(
        showSnackbar(
          Feedback.ERROR,
          intl.formatMessage({
            id: 'explorer.explorerMenuBarContainer.uploadProtocol.fail.invalidFile',
          }),
        ),
      );
      dispatch(hideLoading());
    }
  };

  const doOpenProtocol = async (selectedProtocol) => {
    if (!isInstrumentOperational) {
      dispatch(showModalType(ModalType.CANNOT_USE_THIS_FEATURE));
      return;
    }

    const loadingMessage = intl.formatMessage({id: 'explorer.loading.openingProtocol'});

    dispatch(showLoading(loadingMessage));

    if (selectedProtocol.protocol?.id !== runningProtocolId) {
      await dispatch(openProtocol(selectedProtocol.protocol, handleRefreshClick));
    } else {
      await dispatch(recoverRunningProtocol());
    }
    dispatch(hideLoading());
  };

  const doOpenProtocolFolder = async (folder) => {
    dispatch(setCurrentProtocolFolder(folder?.folder));
  };

  const handleOpenButtonClick = () => {
    const selectedItem = protocolList.find((p) => p.isSelected || selectedProtocolAndFolderIds.includes(p.ID));
    if (!selectedItem) return;

    if (selectedItem.type === ExplorerContentTypes.FOLDER) {
      doOpenProtocolFolder(selectedItem);
    } else {
      doOpenProtocol(selectedItem);
    }
  };

  const newProtocolClick = (isTessaraProtocol = false) => {
    if (!isInstrumentOperational) {
      dispatch(showModalType(ModalType.CANNOT_USE_THIS_FEATURE));
      return;
    }

    dispatch(globalUndoRedoClearItem());
    dispatch(newProtocol(false, isTessaraProtocol));
  };

  const onShowProtocolInfo = () => {
    dispatch(showComingSoonDialog(intl.formatMessage({id: 'modal.showComingSoonDialog.title.protocolInfo'})));
  };

  const onShareProtocolHandler = async (isRunOnly = false) => {
    if (isRunOnly) {
      const folderIds = selectedFolders.map((p) => p.ID);
      if (folderIds.length) {
        const folderHasTessaraProtocol = await dispatch(
          isFolderContainsTessaraProtocol(folderIds, sessionUser.userId, () =>
            setSendProtocolModal({visible: true, isRunOnly}),
          ),
        );
        if (folderHasTessaraProtocol) return;
      }
    }
    setSendProtocolModal({visible: true, isRunOnly});
  };

  const onConvertToTessaraProtocol = useCallback(() => {
    const onUpdateProtocol = (protocol) =>
      new Promise((resolve) => {
        (async () => {
          const {id, isTessaraProtocol} = protocol;

          const res = await dispatch(updateProtocolState(id, {IsTessaraProtocol: !isTessaraProtocol}));
          resolve(res);
        })();
      });

    const sampleProtocol = selectedItems[0];
    const toTessaraProtocol = !sampleProtocol?.protocol?.isTessaraProtocol;

    const updatedProtocols = [];

    dispatch(
      showModalConvertToTessaraProtocol(async () => {
        let isConvertingOpenedProtocol;

        const promises = selectedItems.map(async (protocol) => {
          const {protocol: rawProtocol} = protocol;
          if (rawProtocol?.id === lastOpenedProtocol?.id) {
            isConvertingOpenedProtocol = true;
          }

          return updatedProtocols.push(await onUpdateProtocol(rawProtocol));
        });
        await Promise.all(promises);

        if (isConvertingOpenedProtocol) {
          dispatch(setLastOpenedProtocol({...lastOpenedProtocol, isTessaraProtocol: toTessaraProtocol}));
        }

        refreshExplorer();
      }, toTessaraProtocol),
    );
  }, [dispatch, lastOpenedProtocol, refreshExplorer, selectedItems]);

  const showProtocolContextMenu = useCallback(
    ({protocolId, itemIndex}) => {
      const isClickingSelectedProtocol = selectedProtocolOrFolderIDs.indexOf(protocolId) !== -1;

      // clicking empty space
      if (!protocolId) {
        selectMultiProtocols([]);
        return;
      }

      if (!isClickingSelectedProtocol) {
        selectMultiProtocols([protocolId]);
        lastSelectedProtocolIdx.current = itemIndex;
      }
    },
    [selectedProtocolOrFolderIDs, selectMultiProtocols],
  );

  const explorerViewModeChange = async (value) => {
    dispatch(showLoading('Please wait...'));
    await dispatch(setExplorerViewSettings(value));
    dispatch(hideLoading());
  };

  const generateUserData = () => {
    const userProfile = sessionUser;
    const availableRoles = userManagementAvailableRoles;
    const serviceRoleId = availableRoles.filter((e) => e.level === UserRoleLevel.SERVICE)[0]?.id;
    const operatorRoleId = availableRoles.filter((e) => e.level === UserRoleLevel.OPERATOR)[0]?.id;

    const userData = availableUsers
      .filter(
        (item) =>
          // filter for Share Protocol (Run Only)
          (sendProtocolModal.isRunOnly && item.userRole !== serviceRoleId && item.userId !== userProfile.userId) ||
          // filter for Send A Copy
          (!sendProtocolModal.isRunOnly &&
            item.userId !== userProfile.userId &&
            item.userRole !== serviceRoleId &&
            item.userRole !== operatorRoleId),
      )
      .map((item) => {
        const roleLevel = availableRoles.find((e) => e.id === item.userRole)?.level;
        return {
          id: item.userId,
          name: item.username,
          role: UserRole[roleLevel],
        };
      });

    return userData;
  };

  const getSendProtocolTitle = () => {
    if (sendProtocolModal.isRunOnly) {
      return 'explorer.ExplorerMenuBarContainer.shareProtocolAndFolder.title';
    }
    return 'explorer.ExplorerMenuBarContainer.sendProtocolModal.title';
  };

  const getSendProtocolConfirmButtonLabel = () => {
    if (sendProtocolModal.isRunOnly) {
      return 'explorer.ExplorerMenuBarContainer.shareProtocolAndFolder.sendButtonLabel';
    }
    return 'explorer.ExplorerMenuBarContainer.sendProtocolModal.sendButtonLabel';
  };

  const sendProtocolModalSendButtonClick = async (targetUserIds, usersList) => {
    const loadingMessage = intl.formatMessage({
      id: 'explorer.explorerMenuBarContainer.sendProtocolModal.loadingMessage',
    });
    dispatch(showLoading(loadingMessage));

    const {protocols, folders} = getDataProtocolsAndFolders(selectedItems);
    const protocolIds = protocols.map((e) => e.id);
    const folderIds = folders.map((e) => e.id);
    if (sendProtocolModal.isRunOnly) {
      const listofUnsharedUsers = usersList
        .filter((e) => !targetUserIds.includes(e.id) && listOfSharedUsers.includes(e.id))
        .map((e) => e.id);
      await dispatch(shareUnshareProtocolsAndFolders(protocolIds, folderIds, targetUserIds, listofUnsharedUsers));
      setSendProtocolModal({visible: false, isRunOnly: false});

      refreshExplorer();
      return;
    }
    await dispatch(sendProtocolsAndFoldersToUsers(protocolIds, folderIds, targetUserIds));

    setSendProtocolModal({visible: false, isRunOnly: false});
  };

  const sendProtocolModalCancelButtonClick = () => {
    setSendProtocolModal({visible: false, isRunOnly: false});
  };

  const doFetchUsers = async () => {
    const response = await getUsersWithLicenseCheck();

    if (!response) {
      return;
    }

    setAvailableUsers(response);
  };

  const handleClickProtocol = (clickedProtocol) => {
    const sortedProtocolList = _.orderBy([...protocolList], ['groupView']);

    const {toBeSelectedIds, clickedIndex} = getExplorerSelectionData(
      sortedProtocolList,
      [clickedProtocol],
      lastSelectedProtocolIdx.current,
      {ctrlKeyPressed, shiftKeyPressed},
    );
    // set selected protocol or folder to redux
    dispatch(setSelectedProtocolOrFolder(toBeSelectedIds));

    lastSelectedProtocolIdx.current = clickedIndex;
    selectMultiProtocols(toBeSelectedIds);
  };

  const onSelectionFinish = (items) => {
    if (!items?.length) {
      // condition when the popup menu or modal is not shown
      if (!removeProtocolModalVisible && !renameProtocolModalVisible && !sendProtocolModal?.visible) {
        // to remove all selected items
        selectMultiProtocols([], true);
      }
      return;
    }

    const sortedProtocolList = _.orderBy([...protocolList], ['type']);
    const selectedItems = items.map((item) => item.props.selectableGroupItem);

    const {toBeSelectedIds, clickedIndex} = getExplorerSelectionData(
      sortedProtocolList,
      selectedItems,
      lastSelectedProtocolIdx.current,
      {ctrlKeyPressed, shiftKeyPressed},
    );

    lastSelectedProtocolIdx.current = clickedIndex;
    selectMultiProtocols(toBeSelectedIds);
  };

  const doubleClickProtocol = async (item) => {
    if (!item) {
      return;
    }
    if (item.type === ExplorerContentTypes.FOLDER) {
      doOpenProtocolFolder(item);
      return;
    }
    if (!isInstrumentOperational) {
      dispatch(showModalType(ModalType.CANNOT_USE_THIS_FEATURE));
      return;
    }
    const loadingMessage = intl.formatMessage({id: 'explorer.loading.openingProtocol'});
    dispatch(showLoading(loadingMessage));

    const rawProtocolData = item.protocol;

    if (rawProtocolData.id !== runningProtocolId) {
      await dispatch(openProtocol(rawProtocolData, handleRefreshClick));
    } else {
      await dispatch(recoverRunningProtocol());
    }
    dispatch(hideLoading());
  };

  const newFolderClick = () => {
    setCreateFolderModalVisible(true);
  };

  const handleCloseCreateFolderModal = () => {
    setCreateFolderModalVisible(false);
  };

  const handleCreateNewFolder = async (folderName) => {
    const loadingMessage = intl.formatMessage({id: 'explorer.createNewFolder.loadingMessage'});
    dispatch(showLoading(loadingMessage));
    setCreateFolderModalVisible(false);

    const response = await dispatch(createProtocolFolder(folderName));

    if (response?.code === ServiceResponseCode.OK) {
      await refreshExplorer();

      const newFolderId = response.payload?.id;
      setToSelectNewProtocolIds([newFolderId]);
      dispatch(hideLoading());

      const snackbarMessage = intl.formatMessage({id: 'explorer.createNewFolder.successMessage'});
      dispatch(showSnackbar(Feedback.SUCCESS, snackbarMessage));
    } else {
      dispatch(hideLoading());
    }
  };

  const handleCloseRemoveProtocolModal = () => {
    setRemoveProtocolModalVisible(false);
  };

  const handleClosePlateSelectionModal = () => {
    setPlateSelectionModalVisible(false);
    setPlateSelectionModalData([]);
    setUploadKey('');
  };

  const handleConfirmPlateSelectionModal = (mappedLabwareIDs) => {
    dispatch(
      showLoading(
        intl.formatMessage({
          id: 'explorer.explorerMenuBarContainer.uploadProtocol.loadingMessage',
        }),
      ),
    );
    confirmUploadProtocol(mappedLabwareIDs, null, () => {
      setPlateSelectionModalVisible(false);
      setPlateSelectionModalData([]);
      setUploadKey('');
    });
  };

  const handleConfirmRemoveProtocols = async () => {
    setRemoveProtocolModalVisible(false);

    const {protocols, folders} = getDataProtocolsAndFolders(selectedItems);

    let type = 'item';
    if (selectedItems.every((p) => p.type === ExplorerContentTypes.FOLDER)) {
      type = 'folder';
    } else if (selectedItems.every((p) => p.type === ExplorerContentTypes.PROTOCOL)) {
      type = 'protocol';
    }

    const loadingMessage = intl.formatMessage(
      {id: 'explorer.modal.remove.loadingMessage'},
      {protocolCount: selectedItems.length, type},
    );
    dispatch(showLoading(loadingMessage));

    const response = await dispatch(deleteProtocolsAndFolders(protocols, folders));

    if (
      response.code === ServiceResponseCode.UNAUTH_ACTION_BECAUSE_NOT_TESSARA ||
      response.code === ServiceResponseCode.UNAUTH_FOLDER_ACTION_BECAUSE_NOT_TESSARA
    ) {
      dispatch(hideLoading());
      dispatch(showSnackbar(Feedback.ERROR, intl.formatMessage({id: 'explorer.download.error.tessaraInsideFolder'})));
      return;
    }

    if (response.code !== ServiceResponseCode.OK) {
      dispatch(hideLoading());
      return;
    }

    const {protocolIds: deletedProtocolIds} = response.payload;

    const lastOpenedProtocolId = lastOpenedProtocol?.id;
    const unsavedProtocolId = lastProtocolCheckpoint?.openedProtocol?.id;

    // remove last opened protocol
    if (
      lastOpenedProtocolId === NEW_PROTOCOL_ID ||
      (lastOpenedProtocolId && deletedProtocolIds.some((e) => e === lastOpenedProtocolId || e === unsavedProtocolId))
    ) {
      dispatch(setLastOpenedProtocol(null));

      // Check if deleting unsaved protocol
      if (!protocolEditorStateAutosave) {
        dispatch(resetProtocolPendingSaveStates());
      }
    }

    await refreshExplorer();
    dispatch(hideModal());
    dispatch(hideLoading());

    const snackbarMessage = intl.formatMessage(
      {id: 'explorer.removeProtocolModal.toast.successMessage'},
      {itemCount: selectedItems.length, type},
    );
    dispatch(showSnackbar(Feedback.SUCCESS, snackbarMessage));
  };

  const handleCutClick = () => {
    if (!selectedItems || !selectedItems.length) return;

    const {protocols, folders} = getProtocolsAndFolders(selectedItems);
    dispatch(cutProtocolsAndFoldersToClipboard(protocols, folders));
  };

  const handleCopyClick = () => {
    if (!selectedItems || !selectedItems.length) return;

    const {protocols, folders} = getProtocolsAndFolders(selectedItems);
    dispatch(copyProtocolsAndFoldersToClipboard(protocols, folders));
  };

  const showMaximumFolderReachedModal = async () => {
    dispatch(
      showModal(
        intl.formatMessage({
          id: 'explorer.modal.maximumFolderReached.title',
        }),
        intl.formatMessage({id: 'explorer.modal.maximumFolderReached.text'}),
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              dispatch(hideModal());
            },
            text: intl.formatMessage({
              id: 'explorer.modal.maximumFolderReached.firstButton',
            }),
          },
        ],
        () => dispatch(hideModal()),
        '',
        false,
      ),
    );
  };

  const showInvalidDestinationFolderModal = async () => {
    dispatch(
      showModal(
        intl.formatMessage({
          id: 'explorer.modal.invalidDestinationFolder.title',
        }),
        intl.formatMessage({id: 'explorer.modal.invalidDestinationFolder.text'}),
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              dispatch(hideModal());
            },
            text: intl.formatMessage({
              id: 'explorer.modal.invalidDestinationFolder.firstButton',
            }),
          },
        ],
        () => dispatch(hideModal()),
        '',
        true,
      ),
    );
  };

  const handlePasteClick = async (folderId = undefined) => {
    let targetFolderId = null;

    if (folderId === undefined) {
      const currentSelectedFolder = protocolList.find(
        (p) => (p.isSelected || selectedProtocolAndFolderIds.includes(p.ID)) && p.type === ExplorerContentTypes.FOLDER,
      );
      if (currentSelectedFolder) {
        targetFolderId = currentSelectedFolder.ID;
      } else {
        targetFolderId = currentProtocolFolder?.id || null;
      }
    } else {
      targetFolderId = folderId;
    }

    const response = await dispatch(pasteProtocolsAndFoldersFromClipboard(targetFolderId));

    if (!response) {
      return;
    }

    if (response.code === ServiceResponseCode.REACH_FOLDER_LEVEL_LIMIT) {
      showMaximumFolderReachedModal();
      return;
    }
    if (response.code === ServiceResponseCode.FOLDER_TARGET_AND_DESTINATION_ARE_SAME) {
      showInvalidDestinationFolderModal();
      return;
    }

    if (response.code === ServiceResponseCode.UNAUTH_ACTION_BECAUSE_NOT_TESSARA) {
      dispatch(showSnackbar(Feedback.ERROR, intl.formatMessage({id: 'explorer.download.error.tessaraInsideFolder'})));
      return;
    }

    await refreshExplorer();

    if (response.code === ServiceResponseCode.OK && response.payload) {
      const newProtocolFolders = response.payload.protocolFolders || [];
      const newProtocols = response.payload.protocols || [];
      const newItemIds = [...newProtocolFolders, ...newProtocols].map((e) => e.id);

      setToSelectNewProtocolIds(newItemIds);
    }
  };

  const handleRefreshClick = async () => {
    await refreshExplorer();
    selectMultiProtocols([]);
  };

  const isMultiTypeSelectedProtocols = () => {
    const protocolsType = [
      ...new Set(
        protocolList
          .filter((p) => p.isSelected || selectedProtocolAndFolderIds.includes(p.ID))
          .map((p) => Boolean(p?.protocol?.isTessaraProtocol)),
      ),
    ];

    const isMultiProtocols = protocolsType.length > 1;

    return isMultiProtocols;
  };

  const checkIsRunningProtocolOrFolder = useCallback(
    (itemId, type) => {
      if (!runningProtocolId) return false;
      if (type === ExplorerContentTypes.FOLDER) {
        return runningFolderIds.includes(itemId);
      }
      return itemId === runningProtocolId;
    },
    [runningFolderIds, runningProtocolId],
  );

  const handleProtocolDropped = async (dragProtocolId, dropProtocolId) => {
    const nonRunningProtocols = selectedItems.filter((e) => !checkIsRunningProtocolOrFolder(e.ID, e.type));
    const {protocols, folders} = getDataProtocolsAndFolders(nonRunningProtocols);
    const destDropFiles = protocolList.find((item) => item.ID === dropProtocolId);

    const isDropToSharedFolder =
      destDropFiles?.type === ExplorerContentTypes.FOLDER && destDropFiles?.folder?.owner !== sessionUser?.userId;
    if (isDropToSharedFolder) {
      const snackbarMessage = <FormattedHTMLMessage id="explorer.moveProtocolsToSharedFolder.errorMessage.text" />;

      dispatch(showSnackbar(Feedback.ERROR, snackbarMessage));
      return;
    }

    dispatch(showLoading(intl.formatMessage({id: 'explorer.moveProtocols.loadingMessage'})));

    const isTessaraProtocolInSelection = await checkTessaraProtocolDroppedInSharedFolder(protocols, dropProtocolId);
    if (isTessaraProtocolInSelection) {
      dispatch(hideLoading());
      dispatch(
        showSelectedProtocolHasTessaraProtocolOnDropOnSharedFolder(() =>
          confirmDropTessaraProtocolInSharedFolder(protocols, folders, dropProtocolId),
        ),
      );
    } else {
      confirmDropTessaraProtocolInSharedFolder(protocols, folders, dropProtocolId);
    }
  };

  const confirmDropTessaraProtocolInSharedFolder = async (protocols, folders, dropProtocolId) => {
    dispatch(showLoading(intl.formatMessage({id: 'explorer.moveProtocols.loadingMessage'})));
    const response = await dispatch(moveProtocolsAndFolders(protocols, folders, dropProtocolId));

    if (!response || response.code !== ServiceResponseCode.OK) {
      if (response?.code === ServiceResponseCode.REACH_FOLDER_LEVEL_LIMIT) {
        showMaximumFolderReachedModal();
      }

      return;
    }

    await refreshExplorer();

    dispatch(hideLoading());

    const folderCount = folders.length;
    const protocolCount = protocols.length;
    let successMessage;
    if (folderCount > 1 && protocolCount > 1) {
      successMessage = intl.formatMessage(
        {id: 'explorer.moveProtocols.successMessage.foldersAndProtocols'},
        {folderCount, protocolCount},
      );
    } else if (folderCount > 1 && protocolCount === 1) {
      successMessage = intl.formatMessage(
        {id: 'explorer.moveProtocols.successMessage.foldersAndProtocol'},
        {folderCount, protocolCount},
      );
    } else if (folderCount === 1 && protocolCount > 1) {
      successMessage = intl.formatMessage(
        {id: 'explorer.moveProtocols.successMessage.folderAndProtocols'},
        {folderCount, protocolCount},
      );
    } else if (folderCount === 1 && protocolCount === 1) {
      successMessage = intl.formatMessage(
        {id: 'explorer.moveProtocols.successMessage.folderAndProtocol'},
        {folderCount, protocolCount},
      );
    } else if (folderCount > 1) {
      successMessage = intl.formatMessage({id: 'explorer.moveProtocols.successMessage.folders'}, {folderCount});
    } else if (folderCount === 1) {
      successMessage = intl.formatMessage({id: 'explorer.moveProtocols.successMessage.folder'}, {folderCount});
    } else if (protocolCount > 1) {
      successMessage = intl.formatMessage({id: 'explorer.moveProtocols.successMessage.protocols'}, {protocolCount});
    } else if (protocolCount === 1) {
      successMessage = intl.formatMessage({id: 'explorer.moveProtocols.successMessage.protocols'}, {protocolCount});
    }

    dispatch(showSnackbar(Feedback.SUCCESS, successMessage));
  };

  useEffect(() => {
    // TODO: initiate protocolState into separated business
    refreshExplorer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protocolEditorStateAutosave, trayID, currentProtocolFolder]);

  const style = showProtocolInfo ? {width: 'calc(100% - 29em)'} : {};
  const isLoading = appLoading?.show;

  const visibleProtocolList = protocolList.filter((e) => e.show);
  const selectedProtocolsCount = selectedItems.length;
  const listOfSharedUsers = useMemo(
    // eslint-disable-next-line no-unsafe-optional-chaining
    () => selectedItems.reduce((acc, curr) => [...new Set([...acc, ...curr?.[curr.type].sharedTo])], []),
    [selectedItems],
  );

  const isSameListofUserForProtocols = useMemo(
    () =>
      selectedItems.every((p) => {
        if (p?.[p.type]?.isTessaraProtocol) return false;
        return listOfSharedUsers.every((t) => p?.[p.type].sharedTo.includes(t));
      }),
    [selectedItems, listOfSharedUsers],
  );

  const isProtocolOwner = useMemo(
    () => selectedItems.every((p) => p?.[p.type]?.owner === sessionUser.userId && !isOperatorRoleLoggedIn),
    [selectedItems, sessionUser, isOperatorRoleLoggedIn],
  );

  const selectedFolders = selectedItems.filter((p) => p.type === ExplorerContentTypes.FOLDER);

  const groupedProtocolList = useMemo(() => {
    const foldersList = [];
    const protocolsList = [];
    const favoriteProtocolsList = [];

    visibleProtocolList.forEach((item) => {
      const {groupView} = item;
      const updatedItem = {...item};

      if (groupView === ProtocolExplorerAccordionGroup.FOLDERS) {
        const show = expandedGroups[ProtocolExplorerAccordionGroup.FOLDERS];
        foldersList.push({...updatedItem, show});
      }

      if (groupView === ProtocolExplorerAccordionGroup.PROTOCOLS) {
        const show = expandedGroups[ProtocolExplorerAccordionGroup.PROTOCOLS];
        protocolsList.push({...updatedItem, show});
      }

      if (groupView === ProtocolExplorerAccordionGroup.FAVORITE_PROTOCOLS) {
        const show = expandedGroups[ProtocolExplorerAccordionGroup.FAVORITE_PROTOCOLS];
        favoriteProtocolsList.push({...updatedItem, show});
      }
    });

    return {
      folders: foldersList,
      protocols: protocolsList,
      favoriteProtocols: favoriteProtocolsList,
    };
  }, [expandedGroups, visibleProtocolList]);

  const handleBreadcrumbClick = async (id, event) => {
    event.stopPropagation();
    if (Number.isNaN(id)) return;

    const clickedBreadcrumbItem = breadcrumbs.find((e) => e.id === id);
    const folder = protocolFolderList.find((item) => item.id === clickedBreadcrumbItem.protocolFolderId);
    doOpenProtocolFolder({folder});
  };

  let content = [];
  const notFoundTemplate = (
    <div className="empty-protocol">
      <img alt="Explorer Empty" src="./svg/explorer/explorer-empty.svg" />
      <p className="orange">{intl.formatMessage({id: 'explorer.noProtocol'})}</p>
    </div>
  );

  if (visibleProtocolList && visibleProtocolList.length > 0) {
    const {folders, protocols, favoriteProtocols} = groupedProtocolList;

    switch (explorerViewMode) {
      case ExplorerViewMode.LIST:
        content = (
          <ListView
            itemsRef={protocolItemsRef}
            folders={folders}
            protocols={protocols}
            favoriteProtocols={favoriteProtocols}
            onCheckIsRunning={checkIsRunningProtocolOrFolder}
            onClickProtocol={handleClickProtocol}
            onDoubleClickProtocol={doubleClickProtocol}
            breadcrumbs={breadcrumbs}
            handleBreadcrumbClick={handleBreadcrumbClick}
            greyedOutProtocolIds={greyedOutProtocolIds}
            onProtocolDropped={handleProtocolDropped}
          />
        );

        break;
      case ExplorerViewMode.DETAIL:
        content = (
          <DetailView
            itemsRef={protocolItemsRef}
            folders={folders}
            protocols={protocols}
            favoriteProtocols={favoriteProtocols}
            onCheckIsRunning={checkIsRunningProtocolOrFolder}
            onClickProtocol={handleClickProtocol}
            onDoubleClickProtocol={doubleClickProtocol}
            handleBreadcrumbClick={handleBreadcrumbClick}
            breadcrumbs={breadcrumbs}
            greyedOutProtocolIds={greyedOutProtocolIds}
            onProtocolDropped={handleProtocolDropped}
          />
        );

        break;
      case ExplorerViewMode.THUMBNAIL:
      default:
        content = (
          <ThumbnailView
            itemsRef={protocolItemsRef}
            folders={folders}
            protocols={protocols}
            favoriteProtocols={favoriteProtocols}
            onCheckIsRunning={checkIsRunningProtocolOrFolder}
            onClickProtocol={handleClickProtocol}
            onDoubleClickProtocol={doubleClickProtocol}
            handleBreadcrumbClick={handleBreadcrumbClick}
            breadcrumbs={breadcrumbs}
            greyedOutProtocolIds={greyedOutProtocolIds}
            onProtocolDropped={handleProtocolDropped}
          />
        );

        break;
    }

    protocolItemsRef.current[visibleProtocolList.length] = protocolContentRef.current;
  } else if (isLoading) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    content = <></>;
  } else if (currentProtocolFolder) {
    content = notFoundTemplate;
  } else {
    content = (
      <div className="empty-protocol">
        <img alt="Explorer Empty" src="./svg/explorer/explorer-empty.svg" />
        <p className="orange">{intl.formatMessage({id: 'explorer.noProtocol'})}</p>
        {isOperatorRoleLoggedIn ? '' : <p className="red">{intl.formatMessage({id: 'explorer.createNewProtocol'})}</p>}
      </div>
    );
  }

  const isContainingTessaraProtocol = useMemo(
    () => selectedItems.find((p) => p?.protocol?.isTessaraProtocol),
    [selectedItems],
  );

  return (
    <PageLayout>
      <PageLayout.Header>
        <ExplorerHeader
          protocolList={protocolList}
          filterByTrayDisabled={trayID === null}
          explorerViewMode={explorerViewMode}
          onSortProtocol={onSortProtocol}
          onFilterProtocolByTrayID={onFilterProtocolByTrayID}
          onFilterProtocolByTrayType={onFilterProtocolByTrayType}
          onFilterTessaraProtocol={onFilterTessaraProtocol}
          onFilterProtocolByName={onFilterProtocolByName}
          onFilterByShareProtocolAndFolder={onFilterByShareProtocolAndFolder}
          onShowAllProtocol={onShowAllProtocol}
          addProtocolClick={newProtocolClick}
          addFolderClick={newFolderClick}
          removeProtocolClick={removeProtocolClick}
          renameProtocolClick={renameProtocolHeaderClick}
          downloadProtocolClick={downloadProtocolClick}
          uploadProtocolClick={uploadProtocolZipClick}
          onOpenButtonClick={handleOpenButtonClick}
          onShowProtocolInfo={onShowProtocolInfo}
          onShareProtocolHandler={onShareProtocolHandler}
          explorerViewModeChange={explorerViewModeChange}
          onCutClick={handleCutClick}
          onCopyClick={handleCopyClick}
          onPasteClick={handlePasteClick}
          onRefreshClick={handleRefreshClick}
          isTessaraProtocol={isContainingTessaraProtocol}
          isMultiTypeSelectedProtocols={isMultiTypeSelectedProtocols()}
          isTessaraSuperUser={dispatch(isRoleLevel(UserRoleLevel.TESSARA_SUPER_USER))}
          convertToTessaraProtocol={onConvertToTessaraProtocol}
          hasTessaraLicense={licenseStateIsTessaraInstrument}
          isSameListofUserForProtocols={isSameListofUserForProtocols}
          canUserPasteProtocolsFolders={canUserPasteProtocolsFolders}
          isProtocolOwner={isProtocolOwner}
        />
      </PageLayout.Header>
      <PageLayout.Content>
        <ProtocolContextMenu
          protocolItemsRef={protocolItemsRef}
          listOfSharedUsers={listOfSharedUsers}
          canUserPasteProtocolsFolders={canUserPasteProtocolsFolders}
          menuList={BaseProtocolMenu}
          onContextMenu={showProtocolContextMenu}
          onRenameClick={renameProtocolHeaderClick}
          onSendProtocol={() => onShareProtocolHandler(false)}
          onShareProtocol={() => onShareProtocolHandler(true)}
          onCutClick={handleCutClick}
          onCopyClick={handleCopyClick}
          onPasteClick={handlePasteClick}
          onDownloadClick={downloadProtocolClick}
          onDeleteClick={removeProtocolClick}
          onConvertTessaraProtocol={onConvertToTessaraProtocol}
        />
        <div style={style} id="protocol-explorer" ref={protocolContentRef}>
          <BreadCrumbs
            items={breadcrumbs}
            onChange={handleBreadcrumbClick}
            show={showBreadCrumbs}
            onProtocolDropped={handleProtocolDropped}
          />
          <SelectableArea
            className="protocol-container"
            style={{height: showBreadCrumbs ? 'calc(100% - 49px)' : '100%'}}
            scrollElementRef={protocolContentRef.current}
            onSelectionFinish={onSelectionFinish}
            disableSelectionOnHover
          >
            {content}
          </SelectableArea>
          <DragPreviewLayer
            selectedItemCount={selectedItems.filter((e) => !checkIsRunningProtocolOrFolder(e.ID, e.type)).length}
          />
        </div>
      </PageLayout.Content>
      {renameProtocolModalVisible && (
        <RenameProtocolModal
          protocol={selectedItems[0]}
          maxCharacters={protocolConfig.nameMaxLength}
          protocolList={protocolList}
          visible={renameProtocolModalVisible}
          renameClick={renameProtocolModalRenameClick}
          cancelClick={renameProtocolModalCloseClick}
          onCloseClick={renameProtocolModalCloseClick}
        />
      )}
      {createFolderModalVisible && (
        <CreateFolderModal
          open={createFolderModalVisible}
          maxLength={protocolConfig.nameMaxLength}
          protocolList={protocolList}
          onCloseClick={handleCloseCreateFolderModal}
          onCancelClick={handleCloseCreateFolderModal}
          onCreateFolderClick={handleCreateNewFolder}
        />
      )}
      {removeProtocolModalVisible && (
        <RemoveProtocolModal
          open
          onRemoveClick={handleConfirmRemoveProtocols}
          onCancelClick={handleCloseRemoveProtocolModal}
          onGotItClick={handleCloseRemoveProtocolModal}
          protocols={selectedItems}
          runningProtocol={runningProtocol}
        />
      )}
      {plateSelectionModalVisisble && (
        <PlateSelectionModal
          onCancelClick={handleClosePlateSelectionModal}
          onConfimClick={handleConfirmPlateSelectionModal}
          maxCharacters={protocolConfig.nameMaxLength}
          data={plateSelectionModalData}
        />
      )}
      {sendProtocolModal.visible && (
        <SendCopyModal
          visible={sendProtocolModal.visible}
          users={generateUserData()}
          protocolCount={selectedProtocolsCount}
          isOperatorOnly={sendProtocolModal.isRunOnly}
          title={intl.formatMessage({
            id: getSendProtocolTitle(),
          })}
          recipientLabel={intl.formatMessage({
            id: 'explorer.ExplorerMenuBarContainer.sendProtocolModal.numberOfRecipientLabel',
          })}
          itemLabel={intl.formatMessage({
            id: 'explorer.ExplorerMenuBarContainer.sendProtocolModal.numberOfItemLabel',
          })}
          recipientUnitLabel="User(s)"
          itemUnitLabel="Item(s)"
          searchFieldPlaceholder={intl.formatMessage({
            id: 'explorer.ExplorerMenuBarContainer.sendProtocolModal.searchFieldPlaceholder',
          })}
          sendButtonLabel={intl.formatMessage({
            id: getSendProtocolConfirmButtonLabel(),
          })}
          cancelButtonLabel={intl.formatMessage({
            id: 'explorer.ExplorerMenuBarContainer.sendProtocolModal.cancelButtonLabel',
          })}
          sendButtonClick={sendProtocolModalSendButtonClick}
          cancelButtonClick={sendProtocolModalCancelButtonClick}
          ref={sendCopyModalRef}
          showDiscription={sendProtocolModal.isRunOnly}
          descriptionMessage={
            <FormattedHTMLMessage id="explorer.ExplorerMenuBarContainer.shareProtocolAndFolder.description" />
          }
          selectedIds={listOfSharedUsers}
        />
      )}
    </PageLayout>
  );
}

ProtocolExplorer.propTypes = {
  intl: PropTypes.any.isRequired,
};

export default withIntl(ProtocolExplorer);
